import React, { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import Marsgpt from "../../assests/snackingRingLogo.png";
import TypingIndicator from "./TypingIndicator";
import AssetCardResponse from "./AssetCardResponse.jsx";
import TableResponse from "./TableResponse";
import SubagentCard, { SubagentProgress } from "./SubagentCard";
import ThinkingBlock from "./ThinkingBlock";
import HierarchyResponse from "./HierarchyResponse.jsx";
import StepsChartResponse from "./StepsChartResponse.jsx";
import LineageResponse from "./LineageResponse.jsx";
import RankedListResponse from "./RankedListResponse.jsx";
import ProgressBarChartResponse from "./ProgressBarChartResponse.jsx";
import InsightCardResponse from "./InsightCardResponse.jsx";
import DerivationResponse from "./DerivationResponse.jsx";
import ChartResponse from "./ChartResponse.jsx";


export default function ChatMessages({ messages = [], userName = "User", isLoading = false }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const initials = userName?.charAt(0).toUpperCase() || "U";

  return (
    <div className="flex flex-col gap-4 w-full">
      {messages.map((msg, i) => (
        <div
          key={i}
          className={`flex items-start gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
        >
          {msg.role === "ai" && (
            <img
              src={Marsgpt}
              alt="AI"
              className="w-[40px] h-[40px] rounded-full  p-[5px]"
            />
          )}
          {msg.role === "user" && (
            <div className="!w-8 !h-8 rounded-full flex items-center justify-center shrink-0 bg-indigo-500 text-white text-xs font-bold">
              {initials}
            </div>
          )}
          <div
            className={`max-w-[90%] px-4 py-3 text-sm leading-relaxed shadow-sm
              ${msg.role === "user"
                ? "bg-indigo-600 text-white rounded-t-2xl rounded-bl-2xl rounded-br-sm"
                : "bg-white text-gray-800 border border-gray-200 rounded-t-2xl rounded-br-2xl rounded-bl-sm"
              }`}
          >
            {msg.role === "ai" ? (
              <>
                {/* ── Model thinking / reasoning ── */}
                {msg.thinking && (
                  <ThinkingBlock
                    content={msg.thinking}
                    isStreaming={!!msg.isStreaming}
                  />
                )}

                {/* ── Subagent cards ── */}
                {msg.subagents && Object.keys(msg.subagents).length > 0 && (
                  <div className="mb-3 space-y-2 border-l-2 border-blue-200 pl-3 ml-1">
                    <SubagentProgress subagents={msg.subagents} />
                    {Object.entries(msg.subagents).map(([id, sa]) => (
                      <SubagentCard key={id} subagent={sa} />
                    ))}
                    {/* Synthesis indicator: all subagents done but still streaming */}
                    {msg.isStreaming &&
                      Object.values(msg.subagents).length > 0 &&
                      Object.values(msg.subagents).every(
                        (s) => s.status === "complete" || s.status === "error"
                      ) && (
                        <div className="flex items-center gap-2 rounded-lg bg-purple-50 px-3 py-1.5 text-[11px] text-purple-700">
                          <span className="animate-spin">&#8635;</span>
                          Synthesizing results from{" "}
                          {Object.keys(msg.subagents).length} subagent
                          {Object.keys(msg.subagents).length !== 1 ? "s" : ""}...
                        </div>
                      )}
                  </div>
                )}

                {/* ── Main content ── */}
                {msg.type === "ASSET_CARD" ? (
                  <>
                    {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                    <AssetCardResponse cards={msg.data} />
                  </>
                ) : msg.type === "TABLE" ? (
                  <>
                    {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                    <TableResponse data={msg.data} />
                  </>
                ) : msg.type === "STEPS_CHART" ? (
                  <>
                    {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                    <StepsChartResponse data={msg.data} />
                  </>
                ) : msg.type === "HIERARCHY" ? (
                  <>
                    {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                    <HierarchyResponse data={msg.data} />
                  </>
                ) :
                  msg.type === "PROGRESS_BAR_CHART" ? (
                    <>
                      {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                      <ProgressBarChartResponse data={msg.data} />
                    </>
                  ) :
                    msg.type === "RANKED_LIST" ? (
                      <>
                        {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                        <RankedListResponse data={msg.data} />
                      </>
                    ) :
                      msg.type === "LINEAGE" ? (
                        <>
                          {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                          <LineageResponse data={msg.data} />
                        </>
                      ) :
                        msg.type === "DERIVATION" ? (
                          <>
                            {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                            <DerivationResponse data={msg.data} />
                          </>
                        ) :
                          msg.type === "INSIGHT_CARD" ? (
                            <>
                              {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                              <InsightCardResponse data={msg.data} />
                            </>
                          ) :
                            msg.type === "CHART" ? (
                              <>
                                {msg.text && <p className="text-sm text-gray-700 mb-2">{msg.text}</p>}
                                <ChartResponse data={msg.data} />
                              </>
                            ) :
                              msg.text ? (
                                <ReactMarkdown
                                  components={{
                                    p: ({ children }) => <p className="mb-2 last:mb-0 break-words">{children}</p>,
                                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                                    ul: ({ children }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                                    ol: ({ children }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                                    li: ({ children }) => <li className="mb-1 break-words">{children}</li>,
                                    code: ({ inline, children }) =>
                                      inline ? (
                                        <code className="bg-gray-100 px-1 rounded text-xs inline-block break-words overflow-hidden">{children}</code>
                                      ) : (
                                        <code className="block bg-gray-100 p-2 rounded text-xs overflow-x-auto mb-2 whitespace-pre-wrap break-words">{children}</code>
                                      ),
                                    pre: ({ children }) => (
                                      <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto mb-2 whitespace-pre-wrap break-words">
                                        {children}
                                      </pre>
                                    ),
                                  }}
                                >
                                  {msg.text}
                                </ReactMarkdown>
                              ) : msg.isStreaming ? (
                                <TypingIndicator size="sm" />
                              ) : null}

                {/* Streaming cursor for main text */}
                {msg.isStreaming && msg.text && (
                  <span className="inline-block h-4 w-0.5 animate-pulse bg-blue-500 rounded ml-0.5" />
                )}
              </>
            ) : (
              msg.text
            )}
          </div>
        </div>
      ))}

      {isLoading && !messages.some((m) => m.isStreaming) && (
        <div className="flex items-start gap-2">
          <img
            src={Marsgpt}
            alt="AI"
            className="w-[40px] h-[40px] rounded-full  p-[5px]"
          />
          <div className="max-w-[90%] px-4 py-3 text-sm leading-relaxed shadow-sm bg-white text-gray-800 border border-gray-200 rounded-t-2xl rounded-br-2xl rounded-bl-sm">
            <TypingIndicator />
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}